import React, { useCallback, useEffect, useRef, useState } from 'react';

/** Thin #8a8a8a pill on profile + pitch deck (Favorites + dashboard). */
const THUMB_WIDTH = 4;
const MIN_THUMB_PX = 20;
/** Peter: thumbs a bit shorter than full proportional. */
const DEFAULT_LENGTH_SCALE = 1.0;
/**
 * Profile card + pitch deck: fixed fraction of the track so the pill does not
 * grow/shrink with content length (same on dashboard and Favorites).
 */
export const PROFILE_PITCH_THUMB_TRACK_FRACTION = 0.08;
/** Gap between the box edge and the outside pill. */
const OUTSIDE_GAP_PX = 4;

export type PrairiScrollMetrics = {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
};

type PrairiScrollbarProps = {
  scrollRef?: React.RefObject<HTMLElement | null>;
  /** Controlled metrics — use when the panel does not use native overflow (no macOS bar). */
  metrics?: PrairiScrollMetrics | null;
  /** Seek to a scrollTop (required for metrics / transform scrollers so the thumb can drag). */
  onSeek?: (scrollTop: number) => void;
  /** Copy this scroller’s thumb pixel size so both bars look the same. */
  matchRef?: { readonly current: HTMLElement | null };
  /** Element that reveals the bar on hover. Defaults to the scrollbar parent. */
  hoverRootRef?: { readonly current: HTMLElement | null };
  /** Keep the grey pill visible (e.g. pitch deck arrows armed). */
  forceShow?: boolean;
  contentKey?: string | number;
  /** Maximum pixel height of the thumb pill. */
  maxThumbHeight?: number;
  /** Exact pixel height of the thumb pill. */
  thumbHeight?: number;
  /** Pixel width of the thumb pill (defaults to 4). */
  thumbWidth?: number;
  /** Vertical inset from top and bottom of track in pixels. */
  trackInsetY?: number;
  /** Place the pill just outside the right edge of the box. */
  outside?: boolean;
  /** Extra px to clear a padded parent (e.g. deck `p-2`) when `outside`. */
  outsideClearPx?: number;
  /** Scale proportional thumb height (default 0.5 = half as long). */
  lengthScale?: number;
  /**
   * When set, thumb height is this fraction of the track (ignores content
   * length). Use for profile + pitch so Favorites and long/short decks match.
   */
  constantTrackFraction?: number;
  /**
   * Pill is portaled into an outside host column — pin to that host (do not
   * use negative `right`, which clips inside a narrow `w-2` rail).
   */
  inHost?: boolean;
};

function thumbPxFor(
  source: { clientHeight: number; scrollHeight: number },
  trackHeight: number,
  maxThumbHeight?: number,
  exactThumbHeight?: number,
  lengthScale = DEFAULT_LENGTH_SCALE,
  constantTrackFraction?: number
): number {
  if (exactThumbHeight && exactThumbHeight > 0) {
    return Math.min(trackHeight, exactThumbHeight);
  }
  let height: number;
  if (constantTrackFraction && constantTrackFraction > 0) {
    height = Math.min(trackHeight, Math.max(MIN_THUMB_PX, trackHeight * constantTrackFraction));
  } else {
    const ratio = (source.clientHeight / Math.max(source.scrollHeight, 1)) * source.clientHeight;
    const raw = Number.isFinite(ratio) ? ratio : MIN_THUMB_PX;
    height = Math.min(trackHeight, Math.max(MIN_THUMB_PX, raw * lengthScale));
  }
  if (maxThumbHeight && maxThumbHeight > 0) {
    height = Math.min(height, maxThumbHeight);
  }
  return height;
}

/** 4px #8a8a8a thumb — same on profile and pitch deck. */
export const PrairiScrollbar: React.FC<PrairiScrollbarProps> = ({
  scrollRef,
  metrics,
  onSeek,
  matchRef,
  hoverRootRef,
  forceShow = false,
  contentKey,
  maxThumbHeight,
  thumbHeight,
  thumbWidth,
  trackInsetY = 0,
  outside = false,
  outsideClearPx = 0,
  lengthScale = DEFAULT_LENGTH_SCALE,
  constantTrackFraction,
  inHost = false,
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const metricsRef = useRef(metrics);
  metricsRef.current = metrics;
  const lockedHeightRef = useRef(0);
  const thumbStateRef = useRef({ top: 0, height: 0, trackTop: 0, trackHeight: 0 });

  const [thumb, setThumb] = useState({
    top: 0,
    height: 0,
    trackTop: 0,
    trackHeight: 0,
    visible: false,
  });
  const [hovering, setHovering] = useState(false);
  const [scrolling, setScrolling] = useState(false);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    thumbStateRef.current = {
      top: thumb.top,
      height: thumb.height,
      trackTop: thumb.trackTop,
      trackHeight: thumb.trackHeight,
    };
  }, [thumb]);

  useEffect(() => {
    lockedHeightRef.current = 0;
    let scrollHideTimer = 0;

    const readMetrics = (): PrairiScrollMetrics | null => {
      if (metricsRef.current) return metricsRef.current;
      const scrollEl = scrollRef?.current;
      if (!scrollEl) return null;
      return {
        scrollTop: scrollEl.scrollTop,
        scrollHeight: scrollEl.scrollHeight,
        clientHeight: scrollEl.clientHeight,
      };
    };

    const trackParent = (): HTMLElement | null => {
      const scrollEl = scrollRef?.current;
      if (scrollEl instanceof HTMLElement) return scrollEl;
      if (hoverRootRef?.current instanceof HTMLElement) return hoverRootRef.current;
      return null;
    };

    const update = () => {
      const m = readMetrics();
      const parent = trackParent();
      if (!m || !parent) {
        setThumb(t => ({ ...t, visible: false }));
        return;
      }

      const parentBox = parent.getBoundingClientRect();
      const inset = trackInsetY;
      const trackTop = inset;
      const trackHeight = Math.max(0, parentBox.height - inset * 2);
      const canScroll = m.scrollHeight > m.clientHeight + 2;
      if (!canScroll || trackHeight <= 0) {
        setThumb({ top: 0, height: 0, trackTop, trackHeight, visible: false });
        return;
      }

      // Size from this scroller only — matchRef was making the pill jump size on drag.
      let height = thumbPxFor(
        { clientHeight: m.clientHeight, scrollHeight: m.scrollHeight },
        trackHeight,
        maxThumbHeight,
        thumbHeight,
        lengthScale,
        constantTrackFraction
      );
      // Constant thumbs always follow the track; proportional thumbs lock so drag
      // does not shrink the pill when content metrics jitter.
      if (!constantTrackFraction) {
        if (lockedHeightRef.current > 0) {
          height = Math.min(trackHeight, lockedHeightRef.current);
        } else {
          lockedHeightRef.current = height;
        }
      }
      const maxTop = Math.max(0, trackHeight - height);
      const range = m.scrollHeight - m.clientHeight;
      const top = trackTop + (range <= 0 ? 0 : (m.scrollTop / range) * maxTop);
      setThumb({ top, height, trackTop, trackHeight, visible: true });
    };

    const onScroll = () => {
      update();
      setScrolling(true);
      window.clearTimeout(scrollHideTimer);
      scrollHideTimer = window.setTimeout(() => setScrolling(false), 900);
    };

    update();
    const raf = window.setTimeout(update, 50);
    const raf2 = window.setTimeout(update, 300);
    const scrollEl = scrollRef?.current;
    scrollEl?.addEventListener('scroll', onScroll, { passive: true });
    const matchEl = matchRef?.current;
    matchEl?.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    if (scrollEl) ro.observe(scrollEl);
    const parent = trackParent();
    if (parent) ro.observe(parent);
    if (matchEl) ro.observe(matchEl);
    if (scrollEl?.firstElementChild) ro.observe(scrollEl.firstElementChild);
    window.addEventListener('resize', update);
    return () => {
      window.clearTimeout(raf);
      window.clearTimeout(raf2);
      window.clearTimeout(scrollHideTimer);
      scrollEl?.removeEventListener('scroll', onScroll);
      matchEl?.removeEventListener('scroll', update);
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [
    scrollRef,
    matchRef,
    hoverRootRef,
    contentKey,
    trackInsetY,
    maxThumbHeight,
    thumbHeight,
    lengthScale,
    constantTrackFraction,
  ]);

  // Controlled transform scroll: flash the pill while offset is changing.
  useEffect(() => {
    if (!metrics) return;
    setScrolling(true);
    const t = window.setTimeout(() => setScrolling(false), 900);
    return () => window.clearTimeout(t);
  }, [metrics?.scrollTop]);

  // Recompute thumb when controlled metrics change (metrics is not in the mount effect deps).
  useEffect(() => {
    if (!metrics) return;
    const parent =
      (hoverRootRef?.current instanceof HTMLElement && hoverRootRef.current) ||
      (scrollRef?.current instanceof HTMLElement && scrollRef.current) ||
      null;
    if (!parent) return;
    const parentBox = parent.getBoundingClientRect();
    const inset = trackInsetY;
    const trackTop = inset;
    const trackHeight = Math.max(0, parentBox.height - inset * 2);
    const canScroll = metrics.scrollHeight > metrics.clientHeight + 2;
    if (!canScroll || trackHeight <= 0) {
      setThumb(t => ({ ...t, visible: false }));
      return;
    }
    let height = thumbPxFor(
      { clientHeight: metrics.clientHeight, scrollHeight: metrics.scrollHeight },
      trackHeight,
      maxThumbHeight,
      thumbHeight,
      lengthScale,
      constantTrackFraction
    );
    if (!constantTrackFraction) {
      if (lockedHeightRef.current > 0) {
        height = Math.min(trackHeight, lockedHeightRef.current);
      } else {
        lockedHeightRef.current = height;
      }
    }
    const maxTop = Math.max(0, trackHeight - height);
    const range = metrics.scrollHeight - metrics.clientHeight;
    const top = trackTop + (range <= 0 ? 0 : (metrics.scrollTop / range) * maxTop);
    setThumb({ top, height, trackTop, trackHeight, visible: true });
  }, [
    metrics?.scrollTop,
    metrics?.scrollHeight,
    metrics?.clientHeight,
    scrollRef,
    hoverRootRef,
    trackInsetY,
    maxThumbHeight,
    thumbHeight,
    lengthScale,
    constantTrackFraction,
  ]);

  useEffect(() => {
    const root = hoverRootRef?.current ?? scrollRef?.current?.offsetParent;
    if (!(root instanceof HTMLElement)) return;
    const sync = () => setHovering(root.matches(':hover'));
    const onEnter = () => setHovering(true);
    const onLeave = () => setHovering(false);
    sync();
    root.addEventListener('pointerenter', onEnter);
    root.addEventListener('pointerleave', onLeave);
    return () => {
      root.removeEventListener('pointerenter', onEnter);
      root.removeEventListener('pointerleave', onLeave);
    };
  }, [hoverRootRef, scrollRef, contentKey]);

  const seekToClientY = useCallback(
    (clientY: number, thumbHeightPx: number) => {
      const trackEl = trackRef.current;
      if (!trackEl) return;
      const trackBox = trackEl.getBoundingClientRect();
      const maxTop = Math.max(0, trackBox.height - thumbHeightPx);
      if (maxTop <= 0) return;
      const y = clientY - trackBox.top - thumbHeightPx / 2;
      const clamped = Math.max(0, Math.min(maxTop, y));
      const ratio = clamped / maxTop;

      const m = metricsRef.current;
      if (m && onSeek) {
        const range = Math.max(0, m.scrollHeight - m.clientHeight);
        onSeek(ratio * range);
        return;
      }
      const scrollEl = scrollRef?.current;
      if (!scrollEl) return;
      const range = Math.max(0, scrollEl.scrollHeight - scrollEl.clientHeight);
      scrollEl.scrollTop = ratio * range;
    },
    [onSeek, scrollRef]
  );

  const onThumbPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget;
    target.setPointerCapture(event.pointerId);
    lockedHeightRef.current = thumbStateRef.current.height || lockedHeightRef.current;
    setDragging(true);
    setScrolling(true);

    const startY = event.clientY;
    const startTop = thumbStateRef.current.top - thumbStateRef.current.trackTop;
    const heightPx = lockedHeightRef.current || thumbStateRef.current.height;
    const trackHeight = thumbStateRef.current.trackHeight;
    const maxTop = Math.max(0, trackHeight - heightPx);

    const onMove = (ev: PointerEvent) => {
      const delta = ev.clientY - startY;
      const nextTop = Math.max(0, Math.min(maxTop, startTop + delta));
      const ratio = maxTop <= 0 ? 0 : nextTop / maxTop;
      const m = metricsRef.current;
      if (m && onSeek) {
        const range = Math.max(0, m.scrollHeight - m.clientHeight);
        onSeek(ratio * range);
        return;
      }
      const scrollEl = scrollRef?.current;
      if (!scrollEl) return;
      const range = Math.max(0, scrollEl.scrollHeight - scrollEl.clientHeight);
      scrollEl.scrollTop = ratio * range;
    };

    const onUp = (ev: PointerEvent) => {
      target.releasePointerCapture(ev.pointerId);
      target.removeEventListener('pointermove', onMove);
      target.removeEventListener('pointerup', onUp);
      target.removeEventListener('pointercancel', onUp);
      setDragging(false);
    };

    target.addEventListener('pointermove', onMove);
    target.addEventListener('pointerup', onUp);
    target.addEventListener('pointercancel', onUp);
  };

  const onTrackPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    seekToClientY(event.clientY, thumb.height);
  };

  if (!thumb.visible) return null;

  const shown = hovering || scrolling || forceShow || dragging;
  const effectiveWidth = thumbWidth || THUMB_WIDTH;

  return (
    <div
      ref={trackRef}
      className={`absolute z-50 transition-opacity opacity-0 group-hover/deck:opacity-100 group-hover/profile:opacity-100 ${
        shown ? '!opacity-100' : ''
      }`}
      style={{
        top: thumb.trackTop,
        height: thumb.trackHeight,
        // Host rail: fill it. Outside (same parent): hang past the box. Else inset.
        ...(inHost
          ? { right: 0, left: 0, width: '100%' as const }
          : {
              width: effectiveWidth,
              right: outside ? -(OUTSIDE_GAP_PX + outsideClearPx + effectiveWidth) : 4,
            }),
        pointerEvents: 'auto',
        cursor: 'pointer',
      }}
      onPointerDown={onTrackPointerDown}
      role="scrollbar"
      aria-orientation="vertical"
      aria-valuenow={Math.round(thumb.top)}
    >
      <div
        className="absolute inset-x-0 rounded-full bg-[#8a8a8a]"
        style={{
          top: thumb.top - thumb.trackTop,
          height: thumb.height,
          width: effectiveWidth,
          boxShadow: '0 0 0 1px rgba(38, 38, 38, 0.9)',
          cursor: dragging ? 'grabbing' : 'grab',
          touchAction: 'none',
        }}
        onPointerDown={onThumbPointerDown}
      />
    </div>
  );
};
